/**
 * @file ChooseAFile.js
 * @module src/js/components/ChooseAFile
 * @description
 * **A reusable file input component with drag-and-drop support and file preview.**
 * - Allows users to select or drag-and-drop files.
 * - Supports file type restrictions via the `accept` prop.
 * - Displays file name and size after selection.
 * - Provides image preview for image files.
 *
 * @version
 * <p>
 *  2025-11-29: First version with documentation. (Zong-Han Wu, zhwu@eda.ee.ntu.edu.tw)<br/>
 *  2025-12-01: Refactored to use `StyledImg` for image preview. (Zong-Han Wu, zhwu@eda.ee.ntu.edu.tw)<br/>
 *  2025-12-02: Use `useId` to generate unique IDs. (Zong-Han Wu, zhwu@eda.ee.ntu.edu.tw)<br/>
 * </p>
 */
import { useCallback, useEffect, useId, useRef, useState } from "react";
import { useTheme } from "../contexts/ThemeProvider";
import { StyledImg } from "./UtilityComponents";

export default function ChooseAFile({
    index = 0,
    accept = null,
    files,
    setFiles,
    alwaysDark = false,
}) {
    const { theme } = useTheme();
    const inputRef = useRef(null);
    const [isDragging, setIsDragging] = useState(false);
    const [isHovered, setIsHovered] = useState(false);
    const [previewURL, setPreviewURL] = useState(null);
    const reactId = useId(); // unique ID
    
    useEffect(() => {
        if (!files || files.length === 0) return;
        const file = files[index];

        if (!file) {
            setPreviewURL(null);
            if (inputRef.current) {
                inputRef.current.value = ""; // reset the <input type="file">
            }
            return;
        }

        // Normal preview setup
        if (file.type && file.type.startsWith("image/")) {
            const objectURL = URL.createObjectURL(file);
            setPreviewURL(objectURL);
            return () => URL.revokeObjectURL(objectURL);
        } else {
            setPreviewURL(null);
        }
    }, [files, index]);

    const handleFileDrop = useCallback((e) => {
        e.preventDefault(); // Prevent default behavior: open file in browser
        setIsDragging(false);
        const file = e.dataTransfer.files[0];
        if (!file) return;

        // accept can be null or a file extension (e.g. '.pdf', '.jpg', '.docx', '.cpp', ...)
        // accept === null means accept any file type
        if (accept) {
            const fileExt = "." + file.name.split('.').pop().toLowerCase();
            const acceptExt = accept.toLowerCase();
    
            if (acceptExt.includes(', ')) {
                const acceptExtList = acceptExt.split(', ');
                if (!acceptExtList.includes(fileExt)) return;
            } else if (fileExt !== acceptExt) return;
        }

        setFiles(prev =>
            prev.map((f, i) => (i === index ? file : f))
        );
    }, [accept, index, setFiles]);

    return (
        <div
            style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
            }}
        >
            <label
                htmlFor={`upload-${reactId}-${index}`} // Same as id in <input>
                style={{
                    marginBottom: '5px',
                    padding: '8px 15px',
                    cursor: 'pointer',
                    border: isHovered ? '2px dashed #aaa' : `2px dashed ${theme === 'dark' ? '#888' : '#ccc'}`,
                    borderRadius: '5px',
                    whiteSpace: 'nowrap',
                    backgroundColor: isHovered ? (alwaysDark ? 'rgba(255, 255, 255, 0.08)' :
                        (theme === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.04)')) :
                        (isDragging ? '#aaaaaa' : 'transparent'),
                    transition: 'all 0.2s ease-in-out'
                }}
                onDrop={handleFileDrop}
                onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                onDragLeave={() => setIsDragging(false)}
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
            >
                Choose/Drag{accept ? ` (${accept})` : ''}
            </label>
            <input
                ref={inputRef}
                id={`upload-${reactId}-${index}`} // Same as htmlFor in <label>
                type="file"
                accept={accept}
                style={{ display: 'none' }}
                onChange={(e) =>
                    setFiles(prev => 
                        prev.map((file, i) => i === index ? e.target.files[0] : file)
                    )
                }
            />
            {files[index] && files[index].name && files[index].size && (
                <div
                    style={{
                        maxWidth: '320px',
                        padding: '8px 0',
                        overflow: 'scroll',
                        whiteSpace: 'nowrap',
                        marginBottom: '5px',
                        fontSize: '0.7em'
                    }}
                >
                    {files[index].name} ({(
                        files[index].size > 1024 * 1024 * 1024 ?  // If size > 1GB
                            (files[index].size / 1024 / 1024 / 1024).toFixed(2) + ' GB' :
                        files[index].size > 1024 * 1024 ?  // If size > 1MB
                            (files[index].size / 1024 / 1024).toFixed(2) + ' MB' :
                        files[index].size > 1024 ?  // If size > 1KB
                            (files[index].size / 1024).toFixed(2) + ' KB' :
                        files[index].size + ' B'
                    )})
                </div>
            )}
            {previewURL && (
                <StyledImg
                    src={previewURL}
                    alt="preview"
                    preview={true}
                    style={{
                        maxWidth: '100px',
                        maxHeight: '100px',
                        borderRadius: '3px',
                        objectFit: 'cover'
                    }}
                />
            )}
        </div>
    );
}
